import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { SocialLoginOption } from "@redotech/redo-model/auth/social-auth";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import LeftArrow from "@redotech/redo-web/icon-old/left-arrow.svg";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo } from "react";
import { useDocumentTitle } from "usehooks-ts";
import {
  getHeaderText,
  getLoginGiftText,
  getWarrantyText,
} from "./dynamic-text-utils";
import { EmailLink } from "./email-link";
import { Footer } from "./footer";
import { Gift } from "./gift";
import { Header } from "./header";
import { LandingPage } from "./landing-page";
import * as loginStyles from "./login.module.css";
import * as page from "./page.module.css";
import { PortalDisabled } from "./portal-disabled";
import { Register } from "./register";
import { RegisterOtp } from "./register-otp";
import { SocialLoginOptionsSection } from "./social-login-options-section";
import { LoginPage, useLogin } from "./use-login";

export const Login = memo(function Login() {
  const {
    settings,
    isPortalDisabled,
    currentPage,
    purchaseChannel,
    channelName,
    giftButtonClicked,
    registerButtonClicked,
    emailButtonClicked,
    toOnlineRegistration,
    toRetailRegistration,
    onBackGiftClicked,
    onBackZipcodeClicked,
    onBackRegisterClicked,
    onBackRegisterOtpClicked,
    isMobile,
    errorMessage,
  } = useLogin();

  useDocumentTitle(getHeaderText(settings));

  return (
    <Flex
      align="center"
      className={page.page}
      flex={1}
      flexDirection="column"
      id="page"
      justify="center"
      p="xxs"
      position="relative"
      style={{
        backgroundImage: `url('${settings?.theme.background_image}')`,
        backgroundPosition: "center",
        backgroundColor: "#F8F8F8",
      }}
    >
      {isPortalDisabled ? (
        <PortalDisabled color={settings?.theme.home_text_color || "#fff"} />
      ) : (
        <Flex
          bgColor="primary"
          className={loginStyles.card}
          flexDirection="column"
          gap="3xl"
          justify="flex-start"
          m="sm"
          maxW={isMobile ? "full" : "none"}
          minW={isMobile ? "none" : "sm"}
          mt={currentPage === LoginPage.GIFT ? "8xl" : "none"}
          p="4xl"
          radius="md"
        >
          {currentPage === LoginPage.GIFT && (
            <Gift onBack={onBackGiftClicked} />
          )}
          {currentPage === LoginPage.LANDING_PAGE && (
            <LandingPage
              emailButtonClicked={emailButtonClicked}
              giftButtonClicked={giftButtonClicked}
              registerButtonClicked={registerButtonClicked}
              showFooter
              showLogo
            />
          )}
          {currentPage === LoginPage.EMAIL_LINK && (
            <EmailLink onBack={onBackZipcodeClicked} />
          )}
          {currentPage === LoginPage.REGISTER && (
            <Register
              onBack={onBackRegisterClicked}
              toOnlineRegistration={toOnlineRegistration}
              toRetailRegistration={toRetailRegistration}
            />
          )}
          {currentPage === LoginPage.REGISTER_OTP && (
            <RegisterOtp
              channelName={channelName}
              onBack={onBackRegisterOtpClicked}
              purchaseChannel={purchaseChannel}
            />
          )}
          {errorMessage && (
            <Text textAlign="center" textColor="error">
              {errorMessage}
            </Text>
          )}
        </Flex>
      )}

      <StoreReturn className={page.storeReturnBlock} settings={settings} />
    </Flex>
  );
});

const StoreReturn = memo(function StoreReturn({
  className = "",
  settings,
}: {
  className?: string;
  settings?: ReturnAppSettings;
}) {
  return (
    <a
      className={classNames(className, page.storeReturn)}
      href={settings?.storeUrl ? "https://" + settings.storeUrl : ""}
      style={{ color: settings?.theme.home_text_color || "#fff" }}
    >
      {/*todo change the color of this conditionally*/}
      <LeftArrow />
      <div>Back to store</div>
    </a>
  );
});

// For merchant dashboard preview
export const LoginDisplay = memo(function LoginDisplay({
  settings,
  readOnly = true,
  onSubmit,
}: {
  settings: any;
  readOnly?: boolean;
  onSubmit: () => void;
}) {
  const isMobile = false;

  const MockOneTimePasscode = () => {
    const texts = {
      emailLabel: "Email",
      emailHint: "Enter your email",
      sendLink: settings?.returnPortalButtonText || "Send link",
      title: getHeaderText(settings),
      subtitle: "Enter your email to receive a unique login link.",
      merchantName: settings?.merchantName || "",
      emailConfirmationTitle: "Check your email",
      emailConfirmationSubtitle: "",
      didNotReceiveEmail: "Didn't receive an email?",
      giftButton: getLoginGiftText(settings),
      registerButton: getWarrantyText(settings),
      dontHaveEmail: "Log in with an order number",
    };
    const emailInput = "";
    const onEmailChange = () => {};
    const onSendLink = onSubmit;
    const sendLinkDisabled = false;
    const errorUI = null;
    const logo = settings?.theme.logo_image || "";
    const showLogo = true;
    const onBack = undefined;
    const socialLoginOptions: any[] = [SocialLoginOption.GOOGLE];
    const nextUrl = "";
    const showFooter = true;

    const loginOptionsUI = (
      <Flex align="center" flexDirection="column" gap="md" justify="flex-start">
        <RedoButton
          hierarchy="link_underline"
          onClick={onSubmit}
          text={texts.dontHaveEmail}
        />
        <RedoButton
          hierarchy="link_underline"
          onClick={onSubmit}
          text={texts.giftButton}
        />
        <RedoButton
          hierarchy="link_underline"
          onClick={onSubmit}
          text={texts.registerButton}
        />
      </Flex>
    );

    return (
      <Flex align="stretch" flexDirection="column" gap="3xl">
        <Header
          logo={showLogo ? logo : undefined}
          merchantName={texts.merchantName}
          onBack={onBack}
          subtitle={texts.subtitle}
          title={texts.title}
        />
        <Flex
          align="stretch"
          flex={1}
          flexDirection="column"
          gap="2xl"
          grow={1}
          justify="flex-start"
        >
          <RedoTextInput
            fullWidth
            label={texts.emailLabel}
            placeholder={texts.emailHint}
            setValue={onEmailChange}
            size="md"
            value={emailInput}
          />
          <RedoButton
            disabled={sendLinkDisabled}
            hierarchy="primary"
            onClick={() => onSendLink()}
            size="lg"
            text={texts.sendLink}
            theme="normal"
          />
          {errorUI}
        </Flex>
        {socialLoginOptions && socialLoginOptions.length > 0 && (
          <SocialLoginOptionsSection
            nextUrl={nextUrl}
            socialLoginOptions={socialLoginOptions}
          />
        )}
        {loginOptionsUI}
        {showFooter && <Footer centered />}
      </Flex>
    );
  };

  return (
    <Flex
      align="center"
      className={page.page}
      flex={1}
      flexDirection="column"
      id="page"
      justify="center"
      p="xxs"
      position="relative"
      style={{
        backgroundImage: `url('${settings?.theme.background_image}')`,
        backgroundPosition: "center",
        backgroundColor: "#F8F8F8",
      }}
    >
      <Flex
        bgColor="primary"
        className={loginStyles.card}
        flexDirection="column"
        gap="3xl"
        justify="flex-start"
        m="sm"
        maxW={isMobile ? "full" : "none"}
        minW={isMobile ? "none" : "sm"}
        mt="none"
        p="4xl"
        radius="md"
      >
        <MockOneTimePasscode />
      </Flex>

      <StoreReturn className={page.storeReturnBlock} settings={settings} />
    </Flex>
  );
});
